import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { loadUser } from "../../actions/auth";
import Sidebar from "../common/sidebar";
import Footer from "../common/footer";
import "../../assets/css/billing.css";

const FREE_FEATURES = [
  "1 workspace member",
  "Unlimited time tracking",
  "Basic reports",
  "Project & client management",
];

const PRO_FEATURES = [
  "Up to 10 workspace members",
  "Unlimited time tracking",
  "Advanced reports & insights",
  "Project & client management",
  "Attendance & leave management",
  "Overtime tracking",
  "HR reports & CSV export",
  "Invoicing",
  "Priority support",
];

const STATUS_META = {
  active:   { get text() { return tr("Active"); },            color: "#16a34a", bg: "#f0fdf4" },
  trialing: { get text() { return tr("Trial"); },             color: "#2563eb", bg: "#eff6ff" },
  launch_free: { get text() { return tr("Launch Free Access"); }, color: "#1a5c30", bg: "#ecfdf3" },
  past_due: { get text() { return tr("Payment past due"); },  color: "#dc2626", bg: "#fef2f2" },
  canceled: { get text() { return tr("Canceled"); },          color: "#6b7280", bg: "#f9fafb" },
  inactive: { get text() { return tr("Inactive"); },          color: "#6b7280", bg: "#f9fafb" },
};

function CheckIcon() {
  useTranslation();
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="billing-check-icon">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function LockIcon() {
  useTranslation();
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="billing-lock-icon">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

const PLAN_OPTIONS = [
  {
    id: 'pro',
    name: 'Pro',
    get seats() { return tr("Up to 10 members"); },
    monthly: 6,
    yearly: 60,
  },
  {
    id: 'team',
    name: 'Team',
    get seats() { return tr("Up to 25 members"); },
    monthly: 10,
    yearly: 100,
  },
];

export default function BillingPage() {
  useTranslation();
  const auth = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [error, setError] = useState(null);
  const [activating, setActivating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const pollRef = useRef(null);

  const isPro = auth.plan && auth.plan !== "free";
  const launchFreeAccess = Boolean(status?.launch_free_access || auth.launch_free_access);
  const launchFreeUntil = status?.launch_free_until || auth.launch_free_until;
  const hasSubscription = isPro || ['active', 'trialing', 'past_due'].includes(
    status?.stripe_subscription_status || auth.subscription_status
  );

  useEffect(() => {
    axios.get("/api/billing/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  // After Stripe checkout redirect, poll /api/auth/me until plan is active
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || isPro) return;

    setActivating(true);
    let attempts = 0;
    const MAX = 12;

    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get("/api/auth/me", { withCredentials: true });
        if (res.data.plan && res.data.plan !== "free") {
          dispatch(loadUser(res));
          setActivating(false);
          clearInterval(pollRef.current);
        }
      } catch (_) {}
      if (attempts >= MAX) {
        setActivating(false);
        clearInterval(pollRef.current);
      }
    }, 3000);

    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUpgrade() {
    setLoadingCheckout(true);
    setError(null);
    try {
      const { data } = await axios.post("/api/billing/create-checkout-session", { plan: selectedPlan, period: selectedPeriod });
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
      setLoadingCheckout(false);
    }
  }

  async function handleManageBilling() {
    setLoadingPortal(true);
    setError(null);
    try {
      const { data } = await axios.post("/api/billing/create-portal-session");
      window.location.href = data.url;
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
      setLoadingPortal(false);
    }
  }

  const subStatus = status?.stripe_subscription_status || auth.subscription_status || "inactive";
  const statusMeta = STATUS_META[subStatus] || STATUS_META.inactive;

  return (
    <div>
      <Sidebar />
      <div className="billing-page">

        {/* Page header */}
        <div className="billing-page-header">
          <div>
            <h1 className="billing-page-title">{tr("Billing & Subscription")}</h1>
            <p className="billing-page-subtitle">{tr("Manage your Minutezilla plan and payment details.")}</p>
          </div>
        </div>

        {activating && (
          <div className="billing-alert billing-alert--info" style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" className="billing-alert-icon" style={{ animation: "spin 1.2s linear infinite" }}>
              <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round"/>
            </svg>
            <span style={{ color: "#1d4ed8" }}>{tr("Activating your Pro subscription… this usually takes a few seconds.")}</span>
          </div>
        )}

        {error && <div className="billing-error">{error}</div>}

        {hasSubscription && subStatus === "past_due" && (
          <div className="billing-alert billing-alert--warning">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="billing-alert-icon">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>{tr("Your last payment failed. Update your payment method to keep Pro features active.")}</span>
            <button className="billing-alert-btn" onClick={handleManageBilling}>{tr("Update payment method")} </button>
          </div>
        )}

        {/* Main grid */}
        <div className="billing-grid">

          {/* Current plan card */}
          <div className="billing-main-card">
            <div className="billing-plan-top">
              <div className="billing-plan-badge">{isPro ? "PRO" : tr("FREE")}</div>
              <div className="billing-plan-info">
                <div className="billing-plan-name">{isPro ? tr("Pro Plan") : tr("Free Plan")}</div>
                <div
                  className="billing-status-pill"
                  style={{ color: statusMeta.color, background: statusMeta.bg }}
                >
                  <span className="billing-status-dot" style={{ background: statusMeta.color }} />
                  {statusMeta.text}
                </div>
              </div>
              {hasSubscription ? (
                launchFreeAccess ? (
                  <div className="billing-btn billing-btn-outline" style={{ cursor: "default", opacity: 1 }}>{tr("Free until")} {launchFreeUntil ? new Date(launchFreeUntil).toLocaleDateString(getLocale()) : tr("June 1, 2027")}
                  </div>
                ) : (
                  <button className="billing-btn billing-btn-outline" onClick={handleManageBilling} disabled={loadingPortal}>
                    {loadingPortal ? tr("Redirecting…") : tr("Manage Billing")}
                  </button>
                )
              ) : (
                <button className="billing-btn billing-btn-primary" onClick={handleUpgrade} disabled={loadingCheckout}>
                  {loadingCheckout ? tr("Redirecting…") : tr("Upgrade to Pro")}
                </button>
              )}
            </div>

            <div className="billing-features-section">
              <div className="billing-features-label">
                {launchFreeAccess ? tr("Launch access includes") : isPro ? tr("What's included in Pro") : tr("Free plan includes")}
              </div>
              <div className="billing-feature-grid">
                {(isPro ? PRO_FEATURES : FREE_FEATURES).map((f) => (
                  <div className="billing-feature-row" key={f}>
                    <CheckIcon />
                    <span>{tr(f)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Side panel */}
          <div className="billing-side">

            {!isPro && !launchFreeAccess && (
              <div className="billing-upgrade-card">
                <div className="billing-upgrade-card-header">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>{tr("Upgrade your plan")} </div>

                <div className="billing-period-toggle">
                  <button
                    className={`billing-period-btn${selectedPeriod === 'monthly' ? ' active' : ''}`}
                    onClick={() => setSelectedPeriod('monthly')}
                  >{tr("Monthly")} </button>
                  <button
                    className={`billing-period-btn${selectedPeriod === 'yearly' ? ' active' : ''}`}
                    onClick={() => setSelectedPeriod('yearly')}
                  >{tr("Yearly")} <span className="billing-save-badge">{tr("Save 17%")}</span>
                  </button>
                </div>

                <div className="billing-plan-options">
                  {PLAN_OPTIONS.map((p) => {
                    const price = selectedPeriod === 'yearly'
                      ? `€${(p.yearly / 12).toFixed(2)}/seat/mo`
                      : `€${p.monthly}/seat/mo`;
                    return (
                      <button
                        key={p.id}
                        className={`billing-plan-option${selectedPlan === p.id ? ' selected' : ''}`}
                        onClick={() => setSelectedPlan(p.id)}
                      >
                        <div className="billing-plan-option-header">
                          <span className="billing-plan-option-name">{p.name}</span>
                          <span className="billing-plan-option-price">{price}</span>
                        </div>
                        <div className="billing-plan-option-desc">
                          {p.seats}{selectedPeriod === 'yearly' ? ` · billed €${p.yearly}/yr` : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <button className="billing-btn billing-btn-primary billing-btn-full" onClick={handleUpgrade} disabled={loadingCheckout}>
                  {loadingCheckout ? tr("Redirecting to Stripe…") : `Upgrade to ${selectedPlan === 'team' ? 'Team' : 'Pro'}`}
                </button>
                <p className="billing-upgrade-footnote">{tr("Secure checkout via Stripe · Cancel any time")} </p>
              </div>
            )}

            {launchFreeAccess && (
              <div className="billing-info-card">
                <div className="billing-info-title">{tr("Launch offer active")}</div>
                <p className="billing-info-desc">{tr("This workspace has full access to Minutezilla features until")} {launchFreeUntil ? new Date(launchFreeUntil).toLocaleDateString(getLocale()) : tr("June 1, 2027")}.
                </p>
                <div className="billing-info-divider" />
                <a href="mailto:hello@minutezilla.com" className="billing-support-link">{tr("Contact support")} </a>
              </div>
            )}

            {hasSubscription && !launchFreeAccess && (
              <div className="billing-info-card">
                <div className="billing-info-title">{tr("Need help?")}</div>
                <p className="billing-info-desc">{tr("To cancel, change plan, or update payment details, use the billing portal.")} </p>
                <button className="billing-btn billing-btn-outline billing-btn-full" onClick={handleManageBilling} disabled={loadingPortal}>
                  {loadingPortal ? tr("Redirecting…") : tr("Open Billing Portal")}
                </button>
                <div className="billing-info-divider" />
                <a href="mailto:hello@minutezilla.com" className="billing-support-link">{tr("Contact support")} </a>
              </div>
            )}

          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
}
